import {
	Bean,
	Beef,
	CakeSlice,
	Coffee,
	Cookie,
	CookingPot,
	Croissant,
	Donut,
	Drumstick,
	EggFried,
	Fish,
	IceCreamCone,
	type LucideIcon,
	Pizza,
	Salad,
	Sandwich,
	Soup,
	Wheat,
} from 'lucide-react'
import type { RecipeType, SystemTags } from '@hewey/shared'

// The deterministic fallback for each meal type — used when nothing more
// specific matches. Cooked dinners (and untyped mystery recipes) default to a
// pot; lucide has no rice / noodle / pasta / curry glyph, so the pot is our
// honest catch-all for a composed savoury dish.
const TYPE_DEFAULT: Record<RecipeType, LucideIcon> = {
	breakfast: Croissant,
	lunch: Sandwich,
	dinner: CookingPot,
	dessert: CakeSlice,
	snack: Cookie,
	unknown: CookingPot,
}

// The single most descriptive placeholder icon for a recipe, derived
// deterministically from its tags and title. Always returns an icon: a specific
// match if we can find one, otherwise the meal-type default.
//
// Ordered guards, first match wins. Dish shape beats protein for composed dishes
// (a "beef stew" reads better as a pot than a steak), and pasta/rice dishes fall
// to the pot since there's no glyph for them.
export const iconFor = (
	tags: SystemTags | null | undefined,
	title: string,
): LucideIcon => {
	const t = title.toLowerCase()
	const has = (...words: string[]): boolean =>
		words.some((w) => t.includes(w))

	// Desserts and breakfasts are their own worlds — refine within them (the
	// type gate makes the sweet keywords safe), else take the type default.
	if (tags?.type === 'dessert') {
		if (has('ice cream', 'gelato', 'sorbet')) return IceCreamCone
		if (has('cookie', 'biscuit')) return Cookie
		if (has('donut', 'doughnut')) return Donut
		return CakeSlice
	}
	if (tags?.type === 'breakfast') {
		if (has('egg', 'omelet', 'omelette', 'frittata')) return EggFried
		if (has('croissant', 'pastry', 'pancake', 'waffle')) return Croissant
		if (has('coffee', 'latte')) return Coffee
		return Croissant
	}

	// Dish shape from the title. Pasta and rice dishes have no glyph of their
	// own, so they join the pot.
	if (has('pizza', 'calzone', 'focaccia')) return Pizza
	if (has('soup', 'broth', 'chowder', 'bisque', 'ramen', 'pho')) return Soup
	if (
		has(
			'stew',
			'casserole',
			'curry',
			'tagine',
			'chilli',
			'chili',
			'hotpot',
			'braise',
			'goulash',
			'dahl',
			'daal',
			'pasta',
			'spaghetti',
			'bolognese',
			'ragu',
			'lasagne',
			'lasagna',
			'carbonara',
			'tagliatelle',
			'penne',
			'macaroni',
			'gnocchi',
			'risotto',
			'paella',
			'pilaf',
			'pilau',
			'biryani',
		)
	)
		return CookingPot
	if (has('salad', 'slaw')) return Salad
	if (
		has(
			'sandwich',
			'burger',
			'wrap',
			'taco',
			'burrito',
			'quesadilla',
			'panini',
		)
	)
		return Sandwich

	// Protein — tag first, title as backup when the tag is unknown/other.
	if (
		tags?.primaryProtein === 'chicken' ||
		has('chicken', 'turkey', 'poultry')
	)
		return Drumstick
	if (
		tags?.primaryProtein === 'red_meat' ||
		has(
			'beef',
			'steak',
			'lamb',
			'pork',
			'bacon',
			'sausage',
			'mince',
			'meatball',
		)
	)
		return Beef
	if (
		tags?.primaryProtein === 'fish_white' ||
		has(
			'fish',
			'salmon',
			'cod',
			'tuna',
			'haddock',
			'prawn',
			'shrimp',
			'crab',
			'seafood',
		)
	)
		return Fish
	if (
		tags?.primaryProtein === 'beans_legumes' ||
		has('lentil', 'chickpea', 'tofu')
	)
		return Bean

	// Sweet things that weren't typed as dessert (e.g. an untyped "carrot cake").
	// Kept tight to avoid savoury tarts/pies — those normally carry a protein tag
	// and resolve above.
	if (has('cheesecake', 'brownie', 'cupcake', 'muffin', 'cake'))
		return CakeSlice
	if (has('cookie', 'biscuit')) return Cookie
	if (has('ice cream', 'gelato')) return IceCreamCone

	// Sauce we can draw, before giving up to the meal-type default.
	if (tags?.sauceBase === 'broth_stock') return Soup
	if (tags?.sauceBase === 'coconut' || tags?.sauceBase === 'dry_spiced')
		return CookingPot

	// Carbs, only where lucide has a glyph.
	if (tags?.primaryCarb === 'bread_wrap') return Sandwich
	if (tags?.primaryCarb === 'grains') return Wheat

	return TYPE_DEFAULT[tags?.type ?? 'unknown']
}
